import { BehaviorSubject, catchError, Observable, of, Subject, tap } from 'rxjs'

/** Lifecycle status of a command execution. */
export type CommandStatus = 'idle' | 'running' | 'success' | 'error'

/** Result payload emitted after a command runs. */
export interface CommandResult<TData = unknown> {
  success: boolean
  data?: TData
  message?: string
}

/**
 * Root class for every command in the app.
 *
 * A command owns its presentation metadata (`icon`, `label`, `tooltip`),
 * its own validation (`checkArguments`) and gating (`enabled`), and the
 * actual work (`doExecute`). Components react to a command through the
 * `status$` and `result$` streams.
 *
 * @typeParam TArgs   Shape of the arguments the command operates on. Optional
 *                    because some commands take no arguments.
 * @typeParam TResult Shape of the data returned by the command's service call.
 */
export abstract class BaseCommand<TArgs = void, TResult = unknown> {
  /** Unique identifier for the command. */
  abstract readonly id: string
  /** Material icon name. Optional for non-UI commands. */
  readonly icon?: string
  /** Short label. Optional for non-UI commands. */
  readonly label?: string
  /** Tooltip text. Optional for non-UI commands. */
  readonly tooltip?: string

  private readonly statusSubject = new BehaviorSubject<CommandStatus>('idle')
  /** Emits the execution status so components can react (spinners, toasts). */
  readonly status$: Observable<CommandStatus> = this.statusSubject.asObservable()

  private readonly resultSubject = new Subject<CommandResult<TResult>>()
  /** Emits the full result payload after each run. */
  readonly result$: Observable<CommandResult<TResult>> = this.resultSubject.asObservable()

  /** Last result produced, for synchronous access by hosts. */
  lastResult: CommandResult<TResult> | null = null

  /** Current synchronous status value. */
  get status(): CommandStatus {
    return this.statusSubject.value
  }

  /** Validate the arguments. Return false to disable the command. */
  checkArguments(_args?: TArgs): boolean {
    return true
  }

  /** General gating (e.g. subscription, ownership). Return false to disable. */
  enabled(_args?: TArgs): boolean {
    return true
  }

  /** Whether the command may run with the given arguments. */
  canExecute(args?: TArgs): boolean {
    return this.enabled(args) && this.checkArguments(args)
  }

  /** Resolve the tooltip for the given arguments. Override for dynamic text. */
  getTooltip(_args?: TArgs): string {
    return this.tooltip ?? ''
  }

  /** Whether a spinner should replace the icon. Override for async state. */
  showSpinner(_args?: TArgs): boolean {
    return this.status === 'running'
  }

  /**
   * Public entry point. Gates on {@link canExecute}, drives the status
   * stream and delegates the work to {@link doExecute}.
   */
  execute(args?: TArgs): Observable<boolean> {
    if (!this.canExecute(args)) {
      return of(false)
    }

    this.statusSubject.next('running')

    return this.doExecute(args).pipe(
      tap(ok => this.statusSubject.next(ok ? 'success' : 'error')),
      catchError(() => {
        this.statusSubject.next('error')
        return of(false)
      }),
    )
  }

  /** Publish a result to {@link result$} and store it on {@link lastResult}. */
  protected emitResult(result: CommandResult<TResult>): void {
    this.lastResult = result
    this.resultSubject.next(result)
  }

  /** Perform the actual work, typically via a service. Returns success. */
  protected abstract doExecute(args?: TArgs): Observable<boolean>
}
